<template>
    <Dialog
        v-model:visible="visible"
        :style="{ width: '800px' }"
        :header="modalTitle"
        :modal="true"
        :closable="!form.processing"
        @hide="onHide"
    >
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Basic Information -->
            <div class="col-span-2">
                <h4 class="text-sm font-semibold text-surface-500 uppercase mb-3">{{ $t('Basic Information') }}</h4>
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Company') }} <span class="text-red-500">*</span></label>
                <Dropdown
                    v-model="form.company_profile_id"
                    :options="companies"
                    optionLabel="name"
                    optionValue="id"
                    :placeholder="$t('Select a company')"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.company_profile_id }"
                    :disabled="form.processing"
                />
                <small class="p-error" v-if="form.errors.company_profile_id">{{ form.errors.company_profile_id }}</small>
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Account Type') }}</label>
                <Dropdown
                    v-model="form.type"
                    :options="accountTypes"
                    optionLabel="label"
                    optionValue="value"
                    class="w-full"
                    :disabled="form.processing"
                />
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Account Name') }} <span class="text-red-500">*</span></label>
                <InputText
                    v-model="form.name"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.name }"
                    :placeholder="$t('Enter account name')"
                    :disabled="form.processing"
                />
                <small class="p-error" v-if="form.errors.name">{{ form.errors.name }}</small>
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Account Holder') }}</label>
                <InputText
                    v-model="form.account_holder"
                    class="w-full"
                    :placeholder="$t('Enter account holder name')"
                    :disabled="form.processing"
                />
            </div>

            <!-- Bank Details (for BANK and WISE) -->
            <template v-if="showBankFields">
                <div class="col-span-2 mt-2">
                    <h4 class="text-sm font-semibold text-surface-500 uppercase mb-3">{{ $t('Bank Details') }}</h4>
                </div>

                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Bank Name') }}</label>
                    <InputText
                        v-model="form.bank_name"
                        class="w-full"
                        :placeholder="$t('Enter bank name')"
                        :disabled="form.processing"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Bank Code') }}</label>
                    <InputText
                        v-model="form.bank_code"
                        class="w-full"
                        :placeholder="$t('Enter bank code')"
                        :disabled="form.processing"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('IBAN') }}</label>
                    <InputText
                        v-model="form.iban"
                        class="w-full"
                        :placeholder="$t('Enter IBAN')"
                        :disabled="form.processing"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('BIC/SWIFT') }}</label>
                    <InputText
                        v-model="form.bic_swift"
                        class="w-full"
                        :placeholder="$t('Enter BIC/SWIFT code')"
                        :disabled="form.processing"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Account Number') }}</label>
                    <InputText
                        v-model="form.account_number"
                        class="w-full"
                        :placeholder="$t('Enter account number')"
                        :disabled="form.processing"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Agency/Branch') }}</label>
                    <InputText
                        v-model="form.agency_number"
                        class="w-full"
                        :placeholder="$t('Enter agency/branch number')"
                        :disabled="form.processing"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Bank Country') }}</label>
                    <InputText
                        v-model="form.bank_country"
                        class="w-full"
                        :placeholder="$t('Enter country')"
                        :disabled="form.processing"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Bank City') }}</label>
                    <InputText
                        v-model="form.bank_city"
                        class="w-full"
                        :placeholder="$t('Enter city')"
                        :disabled="form.processing"
                    />
                </div>
            </template>

            <!-- PayPal Details -->
            <template v-if="showPaypalFields">
                <div class="col-span-2 mt-2">
                    <h4 class="text-sm font-semibold text-surface-500 uppercase mb-3">{{ $t('PayPal Details') }}</h4>
                </div>

                <div class="col-span-2">
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('PayPal Email') }}</label>
                    <InputText
                        v-model="form.paypal_email"
                        type="email"
                        class="w-full"
                        :class="{ 'p-invalid': form.errors.paypal_email }"
                        :placeholder="$t('Enter PayPal email')"
                        :disabled="form.processing"
                    />
                    <small class="p-error" v-if="form.errors.paypal_email">{{ form.errors.paypal_email }}</small>
                </div>
            </template>

            <!-- Crypto Details -->
            <template v-if="showCryptoFields">
                <div class="col-span-2 mt-2">
                    <h4 class="text-sm font-semibold text-surface-500 uppercase mb-3">{{ $t('Crypto Details') }}</h4>
                </div>

                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Crypto Type') }}</label>
                    <Dropdown
                        v-model="form.crypto_type"
                        :options="cryptoTypes"
                        optionLabel="label"
                        optionValue="value"
                        :placeholder="$t('Select crypto type')"
                        class="w-full"
                        :disabled="form.processing"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Wallet Address') }}</label>
                    <InputText
                        v-model="form.crypto_wallet"
                        class="w-full"
                        :placeholder="$t('Enter wallet address')"
                        :disabled="form.processing"
                    />
                </div>
            </template>

            <!-- Additional Settings -->
            <div class="col-span-2 mt-2">
                <h4 class="text-sm font-semibold text-surface-500 uppercase mb-3">{{ $t('Additional Settings') }}</h4>
            </div>

            <div class="col-span-2">
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Payment Link') }}</label>
                <InputText
                    v-model="form.payment_link"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.payment_link }"
                    :placeholder="$t('Enter payment link URL')"
                    :disabled="form.processing"
                />
                <small class="p-error" v-if="form.errors.payment_link">{{ form.errors.payment_link }}</small>
            </div>

            <div class="col-span-2">
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Accepted Currencies') }} <span class="text-red-500">*</span></label>
                <MultiSelect
                    v-model="form.accepted_currencies"
                    :options="currencies"
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.accepted_currencies }"
                    :placeholder="$t('Select currencies')"
                    :disabled="form.processing"
                />
                <small class="p-error" v-if="form.errors.accepted_currencies">{{ form.errors.accepted_currencies }}</small>
            </div>

            <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Sort Order') }}</label>
                <InputNumber
                    v-model="form.sort_order"
                    class="w-full"
                    :min="0"
                    :disabled="form.processing"
                />
            </div>

            <div class="flex align-items-center">
                <Checkbox
                    v-model="form.is_default"
                    :binary="true"
                    inputId="is_default"
                    :disabled="form.processing"
                />
                <label for="is_default" class="ml-2 font-medium">{{ $t('Default Account') }}</label>
            </div>
        </div>

        <template #footer>
            <Button
                :label="$t('Cancel')"
                icon="pi pi-times"
                text
                @click="close"
                :disabled="form.processing"
            />
            <Button
                :label="isEditing ? $t('Update Account') : $t('Save Account')"
                icon="pi pi-save"
                severity="primary"
                @click="save"
                :loading="form.processing"
                :disabled="form.processing"
            />
        </template>
    </Dialog>
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance } from 'vue';
import { useForm } from '@inertiajs/vue3';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';
import MultiSelect from 'primevue/multiselect';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false
    },
    bankAccount: {
        type: Object,
        default: null
    },
    companies: {
        type: Array,
        default: () => []
    },
    currencies: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits(['update:modelValue', 'saved']);

const visible = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value)
});

const isEditing = computed(() => props.bankAccount && props.bankAccount.id);

const modalTitle = computed(() => {
    return isEditing.value ? $t('Edit Bank Account') : $t('New Bank Account');
});

const accountTypes = [
    { label: 'Bank', value: 'BANK' },
    { label: 'PayPal', value: 'PAYPAL' },
    { label: 'Wise', value: 'WISE' },
    { label: 'Crypto', value: 'CRYPTO' },
];

const cryptoTypes = [
    { label: 'Bitcoin (BTC)', value: 'BTC' },
    { label: 'Ethereum (ETH)', value: 'ETH' },
    { label: 'USDT (TRC20)', value: 'USDT-TRC20' },
    { label: 'USDT (ERC20)', value: 'USDT-ERC20' },
    { label: 'USDC', value: 'USDC' },
];

const form = useForm({
    company_profile_id: null,
    name: '',
    type: 'BANK',
    bank_name: '',
    bank_code: '',
    bank_address: '',
    bank_city: '',
    bank_country: '',
    bank_zipcode: '',
    iban: '',
    bic_swift: '',
    account_number: '',
    agency_number: '',
    account_holder: '',
    paypal_email: '',
    crypto_wallet: '',
    crypto_type: '',
    payment_link: '',
    accepted_currencies: ['USD'],
    is_default: false,
    sort_order: 0,
});

const showBankFields = computed(() => form.type === 'BANK' || form.type === 'WISE');
const showPaypalFields = computed(() => form.type === 'PAYPAL');
const showCryptoFields = computed(() => form.type === 'CRYPTO');

// Watch for bankAccount changes to populate form
watch(() => props.bankAccount, (newBankAccount) => {
    if (newBankAccount) {
        Object.keys(form.data()).forEach(key => {
            if (newBankAccount[key] !== undefined) {
                form[key] = newBankAccount[key];
            }
        });
    } else {
        form.reset();
    }
}, { immediate: true });

// Watch for modal visibility to reset form when opening for new
watch(() => props.modelValue, (isVisible) => {
    if (isVisible && !props.bankAccount) {
        form.reset();
    }
});

const close = () => {
    visible.value = false;
};

const onHide = () => {
    form.reset();
    form.clearErrors();
};

const save = () => {
    form.clearErrors();
    let hasErrors = false;

    if (!form.company_profile_id) {
        form.setError('company_profile_id', $t('Company is required'));
        hasErrors = true;
    }

    if (!form.name || form.name.trim() === '') {
        form.setError('name', $t('Account name is required'));
        hasErrors = true;
    }

    if (!form.accepted_currencies || form.accepted_currencies.length === 0) {
        form.setError('accepted_currencies', $t('Select at least one currency'));
        hasErrors = true;
    }

    if (hasErrors) {
        toast.add({
            severity: 'warn',
            summary: $t('Validation Error'),
            detail: $t('Please fill in all required fields'),
            life: 3000
        });
        return;
    }

    const url = isEditing.value
        ? route('bank-accounts.update', props.bankAccount.id)
        : route('bank-accounts.store');
    const method = isEditing.value ? 'put' : 'post';

    form[method](url, {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: isEditing.value ? $t('Bank account updated successfully') : $t('Bank account created successfully'),
                life: 3000
            });
            visible.value = false;
            emit('saved');
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to save bank account'),
                life: 5000
            });
        }
    });
};
</script>

<style scoped>
.p-error {
    color: var(--red-500);
    font-size: 0.75rem;
    margin-top: 0.25rem;
    display: block;
}
</style>
